// "Which courses has this member done, and what are they meant to be doing."
//
// Each assigned track collapses to one strip: a square per course plus
// "8 of 11". Expand for the numbered checklist with completion dates. Courses
// are checkboxes in practice, so there are no lesson bars here — a lesson count
// appears only inside a course that has more than one.
import { useState } from "react"
import { BookOpen, Check, ChevronDown, ChevronRight, Layers } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"

function formatDate(iso) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

function stateOf(course) {
  if (course.complete) return "complete"
  return course.completed > 0 ? "partial" : "none"
}

function Mark({ course }) {
  const state = stateOf(course)
  if (state === "complete") return <Check className="h-4 w-4 text-emerald-600" aria-label="Complete" />
  if (state === "partial") {
    return (
      <span className="text-[11px] text-amber-700 tabular-nums">
        {course.completed}/{course.total}
      </span>
    )
  }
  return <span className="block h-1.5 w-1.5 rounded-full bg-zinc-300" aria-label="Not started" />
}

function CourseRow({ course }) {
  const date = formatDate(course.lastCompletedAt)
  return (
    <li className="flex items-center gap-2 text-sm py-1">
      {course.step != null && (
        <span className="w-5 text-right text-xs text-zinc-400 tabular-nums">{course.step}.</span>
      )}
      <span className="w-6 flex justify-center shrink-0">
        <Mark course={course} />
      </span>
      <span
        className={`flex-1 min-w-0 truncate ${course.complete ? "text-zinc-500" : "text-zinc-800"}`}
      >
        {course.title}
      </span>
      <span className="text-xs text-zinc-400 shrink-0">
        {course.complete
          ? date
          : course.completed > 0
            ? `${course.completed} of ${course.total} lessons`
            : ""}
      </span>
    </li>
  )
}

function TrackStrip({ track }) {
  const [open, setOpen] = useState(false)

  return (
    <div className="rounded-md border border-zinc-200">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center gap-3 px-3 py-2 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 text-zinc-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-zinc-400 shrink-0" />
        )}
        <Layers className="h-4 w-4 text-kf-red shrink-0" />
        <span className="text-sm font-medium text-zinc-900 flex-1 min-w-0 truncate">
          {track.title}
        </span>
        <span className="hidden sm:flex gap-1 shrink-0">
          {track.courses.map((course) => (
            <span
              key={course.id}
              title={`${course.step}. ${course.title}`}
              className={
                "h-2.5 w-2.5 rounded-sm " +
                (course.complete
                  ? "bg-kf-red"
                  : course.completed > 0
                    ? "bg-amber-400"
                    : "bg-zinc-200")
              }
            />
          ))}
        </span>
        <span className="text-xs text-zinc-500 tabular-nums w-20 text-right shrink-0">
          {track.coursesCompleted} of {track.coursesTotal}
        </span>
      </button>
      {open && (
        <ol className="border-t border-zinc-100 px-3 py-2 grid grid-cols-1 md:grid-cols-2 gap-x-8">
          {track.courses.map((course) => (
            <CourseRow key={course.id} course={course} />
          ))}
        </ol>
      )}
    </div>
  )
}

export function CourseHistoryCard({ history, memberName }) {
  const { tracks = [], individualCourses = [], elsewhere } = history
  const completedElsewhere = elsewhere?.completed || []
  const startedElsewhere = elsewhere?.started || []
  const isEmpty =
    tracks.length === 0 &&
    individualCourses.length === 0 &&
    completedElsewhere.length === 0 &&
    startedElsewhere.length === 0

  return (
    <section className="mt-8">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Courses
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isEmpty ? (
            <p className="text-sm text-zinc-500 italic">
              {memberName} has no courses assigned and none completed yet.
            </p>
          ) : (
            <>
              {tracks.map((track) => (
                <TrackStrip key={track.id} track={track} />
              ))}

              {individualCourses.length > 0 && (
                <div className="pt-1">
                  <p className="text-xs uppercase tracking-wide text-zinc-500 mb-1">
                    Individual courses
                  </p>
                  <ul>
                    {individualCourses.map((course) => (
                      <CourseRow key={course.id} course={course} />
                    ))}
                  </ul>
                </div>
              )}

              {(completedElsewhere.length > 0 || startedElsewhere.length > 0) && (
                <p className="text-xs text-zinc-500 pt-2 border-t border-zinc-100">
                  {completedElsewhere.length > 0 && (
                    <>
                      Also completed in other groups:{" "}
                      {completedElsewhere.map((c) => c.title).join(", ")}
                    </>
                  )}
                  {startedElsewhere.length > 0 && (
                    <span className="text-zinc-400">
                      {completedElsewhere.length > 0 ? " · " : ""}
                      Started: {startedElsewhere.map((c) => c.title).join(", ")}
                    </span>
                  )}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
